// Merchant list + edit dialog + credential modal.
//
// Polished v2: KPI cards at the top (active / with creds / stale),
// shared TableShell + StatusChip from rpaTheme, EmptyState component,
// consistent toolbar styling.
//
// Plaintext credentials NEVER round-trip through this component. Once set,
// the only signal the UI gets is `has_username / has_password / last_rotated_at`.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Typography, Stack, Button, IconButton, Chip, Tooltip,
    Table, TableHead, TableBody, TableRow, TableCell, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions, Switch,
    FormControlLabel, Alert, CircularProgress, InputAdornment, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyIcon from '@mui/icons-material/Key';
import LockResetIcon from '@mui/icons-material/LockReset';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import StorefrontIcon from '@mui/icons-material/Storefront';
import VerifiedIcon from '@mui/icons-material/Verified';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import SearchIcon from '@mui/icons-material/Search';
import TableChartIcon from '@mui/icons-material/TableChart';
import ColumnMappingsDialog from './ColumnMappingsDialog';
import {
    rpaMerchantList, rpaMerchantCreate, rpaMerchantUpdate, rpaMerchantDelete,
    rpaCredentialsSet, rpaCredentialsClear,
} from './api';
import {
    KpiCard, KpiGrid, SectionPaper, TableShell, EmptyState, Toolbar,
    RPA_COLORS, formatDateShort,
} from './rpaTheme';

const EMPTY_FORM = {
    name: '',
    base_url: '',
    login_url: '',
    anti_bot_profile_text: '',
    is_active: true,
};

const STALE_DAYS = 30;
const dayMs = 86400000;

export default function MerchantList() {
    const [merchants, setMerchants] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [credModal, setCredModal] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [mappingsForMerchant, setMappingsForMerchant] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const r = await rpaMerchantList({ search, include_inactive: 1 });
            if (r?.data?.success) setMerchants(r.data.data || []);
            else setError(r?.data?.message || 'Failed to load merchants');
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, [search]);

    useEffect(() => { load(); }, [load]);

    const stats = useMemo(() => {
        const active = merchants.filter((m) => m.is_active);
        const withCreds = active.filter(
            (m) => m.credential_meta?.has_username && m.credential_meta?.has_password
        );
        const stale = active.filter((m) => {
            const last = m.credential_meta?.last_login_at;
            if (!last) return false;
            return (Date.now() - new Date(last).getTime()) / dayMs > STALE_DAYS;
        });
        return {
            total: merchants.length,
            active: active.length,
            withCreds: withCreds.length,
            stale: stale.length,
        };
    }, [merchants]);

    const openNew = () => { setEditing('new'); setForm(EMPTY_FORM); };
    const openEdit = (m) => {
        setEditing(m.id);
        setForm({
            name: m.name || '',
            base_url: m.base_url || '',
            login_url: m.login_url || '',
            anti_bot_profile_text: m.anti_bot_profile
                ? JSON.stringify(m.anti_bot_profile, null, 2) : '',
            is_active: !!m.is_active,
        });
    };
    const closeEdit = () => { setEditing(null); setForm(EMPTY_FORM); };

    const save = async () => {
        setError(null);
        let antiBot = null;
        if (form.anti_bot_profile_text.trim()) {
            try { antiBot = JSON.parse(form.anti_bot_profile_text); }
            catch { setError('Anti-bot profile must be valid JSON.'); return; }
        }
        const payload = {
            name: form.name.trim(),
            base_url: form.base_url.trim(),
            login_url: form.login_url.trim() || null,
            anti_bot_profile: antiBot,
        };
        try {
            if (editing === 'new') {
                const r = await rpaMerchantCreate(payload);
                if (!r?.data?.success) throw new Error(r?.data?.message || 'Create failed');
            } else {
                const r = await rpaMerchantUpdate(editing, { ...payload, is_active: form.is_active });
                if (!r?.data?.success) throw new Error(r?.data?.message || 'Update failed');
            }
            closeEdit();
            load();
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        }
    };

    const onDelete = async () => {
        if (!confirmDelete) return;
        try {
            await rpaMerchantDelete(confirmDelete);
            setConfirmDelete(null);
            load();
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        }
    };

    return (
        <Box>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <KpiGrid>
                <KpiCard
                    label="Active merchants"
                    value={stats.active}
                    icon={<StorefrontIcon fontSize="small" />}
                    accent={RPA_COLORS.accent}
                />
                <KpiCard
                    label="With credentials"
                    value={stats.withCreds}
                    icon={<VerifiedIcon fontSize="small" />}
                    accent={RPA_COLORS.success}
                    hint={stats.active > stats.withCreds
                        ? `${stats.active - stats.withCreds} need creds`
                        : 'All set'}
                />
                <KpiCard
                    label="Stale credentials"
                    value={stats.stale}
                    icon={<HistoryToggleOffIcon fontSize="small" />}
                    accent={RPA_COLORS.warning}
                    hint={`No login in ${STALE_DAYS}+ days`}
                />
                <KpiCard
                    label="Total tracked"
                    value={stats.total}
                    icon={<StorefrontIcon fontSize="small" />}
                    accent={RPA_COLORS.neutral}
                    hint={merchants.length === stats.active ? 'No inactive' :
                        `${merchants.length - stats.active} inactive`}
                />
            </KpiGrid>

            <SectionPaper>
                <Toolbar>
                    <TextField
                        size="small"
                        placeholder="Search merchants…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon fontSize="small"
                                                        sx={{ mr: 1, color: RPA_COLORS.textFaint }} />,
                        }}
                        sx={{ minWidth: 280 }}
                    />
                    <IconButton onClick={load} disabled={loading} size="small">
                        {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                    </IconButton>
                    <Box sx={{ flex: 1 }} />
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
                        Add Merchant
                    </Button>
                </Toolbar>

                <TableShell>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Base URL</TableCell>
                                <TableCell>Credentials</TableCell>
                                <TableCell>Last login</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {merchants.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ p: 0, border: 'none' }}>
                                        <EmptyState
                                            icon={<StorefrontIcon />}
                                            title="No merchants yet"
                                            message="Add your first merchant portal to start training the agent. You'll need the portal URL and credentials."
                                            action={
                                                <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
                                                    Add your first merchant
                                                </Button>
                                            }
                                        />
                                    </TableCell>
                                </TableRow>
                            )}
                            {merchants.map((m) => {
                                const meta = m.credential_meta;
                                const hasCreds = meta?.has_username && meta?.has_password;
                                const isStale = meta?.last_login_at &&
                                    (Date.now() - new Date(meta.last_login_at).getTime()) / dayMs > STALE_DAYS;
                                return (
                                    <TableRow key={m.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {m.name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption"
                                                        sx={{ wordBreak: 'break-all', color: RPA_COLORS.textMuted }}>
                                                {m.base_url}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {hasCreds ? (
                                                <Chip
                                                    label={meta?.last_rotated_at
                                                        ? `Set ${formatDateShort(meta.last_rotated_at)}`
                                                        : 'Set'}
                                                    size="small"
                                                    color={isStale ? 'warning' : 'success'}
                                                    icon={<KeyIcon sx={{ fontSize: 14 }} />}
                                                />
                                            ) : (
                                                <Chip label="Not set" size="small" color="warning" variant="outlined" />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" sx={{
                                                color: isStale ? RPA_COLORS.warning : RPA_COLORS.textMuted,
                                                fontWeight: isStale ? 600 : 400,
                                            }}>
                                                {meta?.last_login_at ? formatDateShort(meta.last_login_at) : '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={m.is_active ? 'Active' : 'Inactive'}
                                                size="small"
                                                color={m.is_active ? 'primary' : 'default'}
                                                variant={m.is_active ? 'filled' : 'outlined'}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Column mappings">
                                                <IconButton size="small"
                                                            onClick={() => setMappingsForMerchant(m)}>
                                                    <TableChartIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Set credentials">
                                                <IconButton size="small" onClick={() => setCredModal(m.id)}>
                                                    <LockResetIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit">
                                                <IconButton size="small" onClick={() => openEdit(m)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Deactivate">
                                                <IconButton size="small" color="error"
                                                            onClick={() => setConfirmDelete(m.id)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableShell>
            </SectionPaper>

            {/* Edit / Create dialog */}
            <Dialog open={editing !== null} onClose={closeEdit} maxWidth="sm" fullWidth>
                <DialogTitle>{editing === 'new' ? 'Add Merchant' : 'Edit Merchant'}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label="Name *" value={form.name}
                                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                                   placeholder="e.g. Acme Utility Co" autoFocus />
                        <TextField label="Base URL *" value={form.base_url}
                                   onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                                   placeholder="https://portal.merchant.com" />
                        <TextField label="Login URL" value={form.login_url}
                                   onChange={(e) => setForm({ ...form, login_url: e.target.value })}
                                   placeholder="https://portal.merchant.com/login" />
                        <TextField label="Anti-bot profile (JSON, optional)"
                                   multiline minRows={3}
                                   value={form.anti_bot_profile_text}
                                   onChange={(e) => setForm({ ...form, anti_bot_profile_text: e.target.value })}
                                   placeholder='{ "user_agent": "...", "viewport": {"width":1280,"height":800} }'
                                   sx={{ fontFamily: 'monospace' }} />
                        {editing !== 'new' && (
                            <FormControlLabel
                                control={<Switch checked={form.is_active}
                                                 onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />}
                                label={form.is_active ? 'Active' : 'Inactive'} />
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeEdit}>Cancel</Button>
                    <Button variant="contained" onClick={save}
                            disabled={!form.name.trim() || !form.base_url.trim()}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <CredentialsDialog merchantId={credModal}
                               onClose={() => { setCredModal(null); load(); }} />

            <ColumnMappingsDialog
                open={mappingsForMerchant !== null}
                merchant={mappingsForMerchant}
                onClose={() => setMappingsForMerchant(null)} />

            <Dialog open={confirmDelete !== null} onClose={() => setConfirmDelete(null)}>
                <DialogTitle>Deactivate this merchant?</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2">
                        This soft-deletes the merchant (sets is_active to false). Audit history,
                        workflows, and runs are preserved. Reactivate any time by editing.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={onDelete}>Deactivate</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}


function CredentialsDialog({ merchantId, onClose }) {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (merchantId !== null) {
            setUsername(''); setPassword(''); setShowPwd(false); setError(null);
        }
    }, [merchantId]);

    const save = async () => {
        if (!username && !password) {
            setError('Provide a username, password, or both.');
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            const payload = {};
            if (username) payload.username = username;
            if (password) payload.password = password;
            const r = await rpaCredentialsSet(merchantId, payload);
            if (!r?.data?.success) throw new Error(r?.data?.message || 'Save failed');
            onClose();
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    const clearAll = async () => {
        if (!window.confirm('Wipe stored credentials and any saved browser session for this merchant?')) {
            return;
        }
        setSubmitting(true);
        try {
            await rpaCredentialsClear(merchantId);
            onClose();
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={merchantId !== null} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Set Credentials</DialogTitle>
            <DialogContent dividers>
                <Alert severity="info" sx={{ mb: 2 }}>
                    Stored encrypted server-side. Plaintext is never written to disk
                    or to logs. Leave a field blank to keep the existing value.
                </Alert>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Stack spacing={2}>
                    <TextField label="Username / Email" value={username}
                               onChange={(e) => setUsername(e.target.value)} autoComplete="off" />
                    <TextField label="Password" type={showPwd ? 'text' : 'password'}
                               value={password}
                               onChange={(e) => setPassword(e.target.value)}
                               autoComplete="new-password"
                               InputProps={{
                                   endAdornment: (
                                       <InputAdornment position="end">
                                           <IconButton onClick={() => setShowPwd(!showPwd)} edge="end">
                                               {showPwd ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                           </IconButton>
                                       </InputAdornment>
                                   ),
                               }} />
                </Stack>
                <Divider sx={{ my: 2 }} />
                <Button color="error" size="small" onClick={clearAll} disabled={submitting}>
                    Clear all stored credentials
                </Button>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button variant="contained" onClick={save} disabled={submitting}>
                    {submitting ? <CircularProgress size={20} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
